import { defineStore } from 'pinia'

import {
  DomainRuleError,
  TRANSITIONS_BY_ID,
  activeLabelOf,
  activeRackBindingOf,
  assertCommandKey,
  assertReconciliationCanConfirm,
  canRegisterException,
  createDemoSnapshot,
  createImmutableEvent,
  deepFreeze,
  deriveOrderStatus,
  reconcileBatchReads,
  registerGarmentException,
  resolveGarmentException,
  roundMoney,
  transitionGarment,
  type AiAdoptableField,
  type AiGarmentSuggestion,
  type AppNotification,
  type BatchReconciliation,
  type DeviceSettings,
  type Evidence,
  type EvidenceKind,
  type FactoryInformationOverride,
  type FactoryInspection,
  type Garment,
  type ImmutableDomainEvent,
  type JsonObject,
  type LabelVersion,
  type Member,
  type MobileSnapshot,
  type Order,
  type Rack,
  type ScanRead,
  type TransitionGuardContext,
  type TransitionId,
} from '../domain'
import { DemoRepository, type MobileRepository } from '../services'

let repository: MobileRepository = new DemoRepository()

/** Configure before creating the Pinia store (tests and future HttpRepository). */
export const configureMobileRepository = (nextRepository: MobileRepository): void => {
  repository = nextRepository
}

let fallbackIdCounter = 0
const nextId = (prefix: string): string => {
  const randomUUID = globalThis.crypto?.randomUUID?.()
  if (randomUUID) return `${prefix}-${randomUUID}`
  fallbackIdCounter += 1
  return `${prefix}-${Date.now()}-${fallbackIdCounter}`
}

const now = (): string => new Date().toISOString()
const commandKey = (scope: string, supplied?: string): string => supplied?.trim() || nextId(`cmd-${scope}`)

const memberDiscountRate = (member: Member): number => {
  if (member.membership === '金卡会员') return 0.9
  if (member.membership === '银卡会员') return 0.95
  return 1
}

const shortCode = (index: number): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const value = `${Date.now()}${fallbackIdCounter}${index}`
  const take = (offset: number) => chars[Number(value.slice(-(offset + 2), -offset || undefined)) % chars.length]
  return `${take(0)}${take(1)}${take(2)}-${take(3)}${take(4)}${take(5)}`
}

const makeDraftLabel = (garmentId: string, garmentShortCode: string): LabelVersion => ({
  id: nextId('label'),
  version: 1,
  status: 'DRAFT',
  barcode: `BC-${garmentShortCode}`,
  epc: `E280-${garmentId.replace(/[^A-Za-z0-9]/g, '').slice(-16).padStart(16, '0')}`,
})

const snapshotFromState = (state: MobileStoreState): MobileSnapshot => ({
  schemaVersion: '1.0.0',
  members: state.members,
  orders: state.orders,
  evidence: state.evidence,
  events: state.events,
  notifications: state.notifications,
  deviceSettings: state.deviceSettings,
  racks: state.racks,
  reconciliations: state.reconciliations,
  processedCommandKeys: state.processedCommandKeys,
})

const orderById = (orders: readonly Order[], orderId: string): Order => {
  const order = orders.find((candidate) => candidate.id === orderId)
  if (!order) throw new DomainRuleError('ORDER_NOT_FOUND', `Order ${orderId} was not found`)
  return order
}

const garmentById = (order: Order, garmentId: string): Garment => {
  const garment = order.garments.find((candidate) => candidate.id === garmentId)
  if (!garment) throw new DomainRuleError('GARMENT_NOT_FOUND', `Garment ${garmentId} was not found in ${order.id}`)
  return garment
}

const replaceGarment = (order: Order, garment: Garment): Order => ({
  ...order,
  garments: order.garments.map((candidate) => candidate.id === garment.id ? garment : candidate),
})

const guardContextFor = (
  order: Order,
  garment: Garment,
  overrides: TransitionGuardContext = {},
): TransitionGuardContext => {
  const allState = order.garments.length > 0 && order.garments.every((candidate) => candidate.workflowState === order.garments[0]?.workflowState)
    ? order.garments[0]?.workflowState
    : undefined
  return {
    pricingBlockersCount: order.pricingReadiness.pricingBlockersCount,
    allImagesUploaded: order.pricingReadiness.allImagesUploaded,
    allRequiredRiskAcknowledgementsConfirmed: order.pricingReadiness.allRequiredRiskAcknowledgementsConfirmed,
    paymentStatus: order.payment.status,
    recalculatedAmount: order.amounts.recalculated,
    confirmedAmount: order.amounts.confirmed,
    activeLabelVersionId: activeLabelOf(garment)?.id,
    initialLabelPrintStatus: order.initialLabelPrintJob.status,
    orderItemCount: order.garments.length,
    dispatchBatchId: order.dispatchBatchId,
    hasActiveRackBinding: Boolean(activeRackBindingOf(garment)),
    garmentStatus: garment.workflowState,
    allOrderGarmentsStatus: allState,
    pickupVerifiedGarmentCount: order.garments.filter((candidate) => candidate.workflowState === 'PICKUP_VERIFYING').length,
    orderGarmentCount: order.garments.length,
    pickupCustomerIdentityConfirmed: order.customerIdentityConfirmed,
    pickupRiskAndQuantityConfirmed: order.pickupRiskAndQuantityConfirmed,
    ...overrides,
  }
}

export interface CreateMobileGarmentInput {
  readonly shortCode?: string
  readonly category: string
  readonly color: string
  readonly material: string
  readonly service: string
  readonly price: number
  readonly conditionNote?: string
  readonly riskFlags?: readonly string[]
}

export interface CreateMobileOrderInput {
  readonly memberId: string
  readonly promisedAt: string
  readonly operatorId: string
  readonly garments: readonly CreateMobileGarmentInput[]
  readonly note?: string
}

export interface RecordAiSuggestionInput {
  readonly orderId: string
  readonly garmentId: string
  readonly suggestion: Omit<AiGarmentSuggestion, 'id' | 'createdAt' | 'review'> & Partial<Pick<AiGarmentSuggestion, 'id' | 'createdAt'>>
}

export interface AddEvidenceInput {
  readonly orderId: string
  readonly garmentId?: string
  readonly kind: EvidenceKind
  readonly uri: string
  readonly thumbnailUri?: string
  readonly note?: string
  readonly capturedBy: string
  readonly capturedAt?: string
  readonly metadata?: JsonObject
}

export interface ConfirmTransitionInput {
  readonly orderId: string
  readonly transitionId: TransitionId
  readonly garmentIds?: readonly string[]
  readonly operatorId: string
  readonly idempotencyKey?: string
  readonly guardContext?: TransitionGuardContext
  readonly metadata?: JsonObject
  readonly dispatchBatchId?: string
}

export interface ReconcileBatchActionInput {
  readonly orderId: string
  readonly reads: readonly ScanRead[]
  readonly transitionId?: TransitionId
  readonly expectedGarmentIds?: readonly string[]
}

export interface MobileStoreState {
  members: readonly Member[]
  orders: readonly Order[]
  evidence: readonly Evidence[]
  events: readonly ImmutableDomainEvent[]
  notifications: readonly AppNotification[]
  deviceSettings: DeviceSettings
  racks: readonly Rack[]
  reconciliations: readonly BatchReconciliation[]
  processedCommandKeys: readonly string[]
  hydrated: boolean
  busy: boolean
  lastError: string | null
}

const initialStoreState = (): MobileStoreState => {
  const seed = createDemoSnapshot()
  return {
    members: [...seed.members],
    orders: [...seed.orders],
    evidence: [...seed.evidence],
    events: [...seed.events],
    notifications: [...seed.notifications],
    deviceSettings: seed.deviceSettings,
    racks: [...seed.racks],
    reconciliations: [...seed.reconciliations],
    processedCommandKeys: [...seed.processedCommandKeys],
    hydrated: false,
    busy: false,
    lastError: null,
  }
}

export const useMobileStore = defineStore('mobile-workflow', {
  state: initialStoreState,

  getters: {
    ordersWithStatus: (state) => state.orders.map((order) => ({ ...order, derivedStatus: deriveOrderStatus(order) })),
    unreadNotificationCount: (state) => state.notifications.filter((notification) => !notification.readAt).length,
    unreadNotifications: (state) => state.notifications.filter((notification) => !notification.readAt),
    orderById: (state) => (id: string) => state.orders.find((order) => order.id === id),
    activeOrderById: (state) => (id: string) => state.orders.find((order) => order.id === id),
    latestReconciliationByOrder: (state) => (id: string) => state.reconciliations.find((item) => item.orderId === id),
  },

  actions: {
    async persist(): Promise<void> {
      await repository.save(snapshotFromState(this))
    },

    applySnapshot(snapshot: MobileSnapshot): void {
      this.members = [...snapshot.members]
      this.orders = [...snapshot.orders]
      this.evidence = [...snapshot.evidence]
      this.events = [...snapshot.events]
      this.notifications = [...snapshot.notifications]
      this.deviceSettings = snapshot.deviceSettings
      this.racks = [...snapshot.racks]
      this.reconciliations = [...snapshot.reconciliations]
      this.processedCommandKeys = [...snapshot.processedCommandKeys]
    },

    async initialize(): Promise<void> {
      if (this.busy) return
      this.busy = true
      this.lastError = null
      try {
        const stored = await repository.load()
        const snapshot = stored ?? createDemoSnapshot()
        this.applySnapshot(snapshot)
        if (!stored) await repository.save(snapshot)
        this.hydrated = true
      } catch (error) {
        this.lastError = error instanceof Error ? error.message : String(error)
        throw error
      } finally {
        this.busy = false
      }
    },

    async resetDemo(): Promise<void> {
      await repository.clear()
      const snapshot = createDemoSnapshot()
      this.applySnapshot(snapshot)
      await repository.save(snapshot)
      this.hydrated = true
      this.lastError = null
    },

    async createMobileOrder(input: CreateMobileOrderInput): Promise<Order> {
      const member = this.members.find((candidate) => candidate.id === input.memberId)
      if (!member) throw new DomainRuleError('MEMBER_NOT_FOUND', `Member ${input.memberId} was not found`)
      if (input.garments.length === 0) throw new DomainRuleError('ORDER_EMPTY', 'A mobile order requires at least one garment')
      if (!input.operatorId.trim()) throw new DomainRuleError('OPERATOR_REQUIRED', 'operatorId is required')

      const occurredAt = now()
      const id = nextId('ORD-M')
      const usedShortCodes = new Set(this.orders.flatMap((order) => order.garments.map((garment) => garment.shortCode)))
      const garments: Garment[] = input.garments.map((draft, index) => {
        if (!Number.isFinite(draft.price) || draft.price < 0) throw new DomainRuleError('INVALID_PRICE', 'Garment price must be a non-negative number')
        const garmentId = nextId('GAR-M')
        const garmentShortCode = draft.shortCode?.trim() || shortCode(index)
        if (usedShortCodes.has(garmentShortCode)) {
          throw new DomainRuleError('SHORT_CODE_DUPLICATE', `Garment shortCode ${garmentShortCode} already exists`)
        }
        usedShortCodes.add(garmentShortCode)
        return {
          id: garmentId,
          shortCode: garmentShortCode,
          orderId: id,
          category: draft.category,
          color: draft.color,
          material: draft.material,
          service: draft.service,
          price: roundMoney(draft.price),
          conditionNote: draft.conditionNote,
          riskFlags: [...(draft.riskFlags ?? [])],
          workflowState: 'DRAFT',
          labels: [makeDraftLabel(garmentId, garmentShortCode)],
          aiSuggestions: [],
          factoryInspections: [],
          factoryOverrides: [],
          rackBindings: [],
          evidenceIds: [],
        }
      })
      const original = roundMoney(garments.reduce((sum, garment) => sum + garment.price, 0))
      const confirmed = roundMoney(original * memberDiscountRate(member))
      const order: Order = {
        id,
        memberId: member.id,
        source: 'MOBILE',
        createdBy: input.operatorId,
        createdAt: occurredAt,
        promisedAt: input.promisedAt,
        note: input.note,
        garments,
        amounts: { original, discount: roundMoney(original - confirmed), confirmed, recalculated: confirmed, currency: 'CNY' },
        pricingReadiness: {
          pricingBlockersCount: garments.filter((garment) => garment.price <= 0).length,
          allImagesUploaded: false,
          allRequiredRiskAcknowledgementsConfirmed: garments.every((garment) => garment.riskFlags.length === 0),
        },
        payment: { id: nextId('PAY-M'), status: 'PENDING', amount: confirmed },
        initialLabelPrintJob: { id: nextId('PRN-M'), status: 'QUEUED', successCount: 0, failedCount: 0 },
        customerIdentityConfirmed: false,
        pickupRiskAndQuantityConfirmed: false,
      }
      this.orders = [order, ...this.orders]
      this.events = [
        ...this.events,
        createImmutableEvent({
          id: nextId('EVT'), type: 'ORDER_DRAFT_CREATED', orderId: order.id, operatorId: input.operatorId,
          occurredAt, metadata: { source: 'MOBILE', garmentCount: garments.length },
        }),
      ]
      await this.persist()
      return order
    },

    async updateDraftGarment(
      orderId: string,
      garmentId: string,
      patch: Partial<Pick<Garment, 'category' | 'color' | 'material' | 'service' | 'price' | 'conditionNote' | 'riskFlags'>>,
    ): Promise<Garment> {
      const order = orderById(this.orders, orderId)
      const garment = garmentById(order, garmentId)
      if (garment.workflowState !== 'DRAFT') throw new DomainRuleError('DRAFT_ONLY', 'Only DRAFT garments can be edited at intake')
      if (patch.price !== undefined && (!Number.isFinite(patch.price) || patch.price < 0)) {
        throw new DomainRuleError('INVALID_PRICE', 'Garment price must be a non-negative number')
      }
      const updated: Garment = { ...garment, ...patch, price: patch.price === undefined ? garment.price : roundMoney(patch.price) }
      let nextOrder = replaceGarment(order, updated)
      const original = roundMoney(nextOrder.garments.reduce((sum, item) => sum + item.price, 0))
      const member = this.members.find((candidate) => candidate.id === order.memberId)!
      const confirmed = roundMoney(original * memberDiscountRate(member))
      nextOrder = {
        ...nextOrder,
        amounts: { original, discount: roundMoney(original - confirmed), confirmed, recalculated: confirmed, currency: 'CNY' },
        payment: { ...nextOrder.payment, amount: confirmed },
        pricingReadiness: {
          ...nextOrder.pricingReadiness,
          pricingBlockersCount: nextOrder.garments.filter((item) => item.price <= 0).length,
        },
      }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? nextOrder : candidate)
      await this.persist()
      return updated
    },

    async setPricingReadiness(orderId: string, readiness: Partial<Order['pricingReadiness']>): Promise<Order> {
      const order = orderById(this.orders, orderId)
      const updated: Order = { ...order, pricingReadiness: { ...order.pricingReadiness, ...readiness } }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updated : candidate)
      await this.persist()
      return updated
    },

    async recordAiSuggestion(input: RecordAiSuggestionInput): Promise<AiGarmentSuggestion> {
      const order = orderById(this.orders, input.orderId)
      const garment = garmentById(order, input.garmentId)
      const suggestion: AiGarmentSuggestion = deepFreeze({
        ...input.suggestion,
        id: input.suggestion.id ?? nextId('AI'),
        createdAt: input.suggestion.createdAt ?? now(),
        riskFlags: [...input.suggestion.riskFlags],
        findings: [...input.suggestion.findings],
      }) as AiGarmentSuggestion
      const updated = { ...garment, aiSuggestions: [...garment.aiSuggestions, suggestion] }
      const nextOrder = replaceGarment(order, updated)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? nextOrder : candidate)
      await this.persist()
      return suggestion
    },

    async adoptAiSuggestion(
      orderId: string,
      garmentId: string,
      suggestionId: string,
      adoptedFields: readonly AiAdoptableField[],
      operatorId: string,
    ): Promise<Garment> {
      const order = orderById(this.orders, orderId)
      const garment = garmentById(order, garmentId)
      if (garment.workflowState !== 'DRAFT') throw new DomainRuleError('AI_REVIEW_DRAFT_ONLY', 'AI intake suggestions can only be reviewed in DRAFT')
      const suggestion = garment.aiSuggestions.find((candidate) => candidate.id === suggestionId)
      if (!suggestion) throw new DomainRuleError('AI_SUGGESTION_NOT_FOUND', `Suggestion ${suggestionId} was not found`)
      if (suggestion.review) throw new DomainRuleError('AI_SUGGESTION_ALREADY_REVIEWED', `Suggestion ${suggestionId} was already reviewed`)
      const fields = [...new Set(adoptedFields)]
      if (fields.length === 0) throw new DomainRuleError('AI_FIELDS_REQUIRED', 'Select at least one AI field to adopt')

      const occurredAt = now()
      let updated: Garment = { ...garment }
      for (const field of fields) {
        if (field === 'riskFlags') updated = { ...updated, riskFlags: [...suggestion.riskFlags] }
        else {
          const value = suggestion[field]
          if (value !== undefined) updated = { ...updated, [field]: value }
        }
      }
      const reviewedSuggestion: AiGarmentSuggestion = deepFreeze({
        ...suggestion,
        review: { decision: 'ADOPTED', reviewedBy: operatorId, reviewedAt: occurredAt, adoptedFields: fields },
      }) as AiGarmentSuggestion
      updated = {
        ...updated,
        aiSuggestions: garment.aiSuggestions.map((candidate) => candidate.id === suggestion.id ? reviewedSuggestion : candidate),
      }
      const nextOrder = replaceGarment(order, updated)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? nextOrder : candidate)
      this.events = [...this.events, createImmutableEvent({
        id: nextId('EVT'), type: 'AI_SUGGESTION_ADOPTED', orderId, garmentId, operatorId, occurredAt,
        metadata: { suggestionId, adoptedFields: fields },
      })]
      await this.persist()
      return updated
    },

    async addEvidence(input: AddEvidenceInput): Promise<Evidence> {
      if (!input.uri.trim()) throw new DomainRuleError('EVIDENCE_URI_REQUIRED', 'Evidence uri is required')
      const order = orderById(this.orders, input.orderId)
      if (input.garmentId) garmentById(order, input.garmentId)
      const capturedAt = input.capturedAt ?? now()
      const item: Evidence = deepFreeze({
        id: nextId('EVD'), orderId: input.orderId, garmentId: input.garmentId, kind: input.kind,
        uri: input.uri, thumbnailUri: input.thumbnailUri, note: input.note, capturedBy: input.capturedBy,
        capturedAt, metadata: input.metadata ?? {},
      }) as Evidence
      this.evidence = [...this.evidence, item]

      let nextOrder = order
      if (input.garmentId) {
        const garment = garmentById(order, input.garmentId)
        nextOrder = replaceGarment(order, { ...garment, evidenceIds: [...garment.evidenceIds, item.id] })
      }
      const evidenceAfter = [...this.evidence]
      const allImagesUploaded = nextOrder.garments.every((garment) => evidenceAfter.some((candidate) =>
        candidate.orderId === nextOrder.id && candidate.garmentId === garment.id && candidate.kind === 'INTAKE_PHOTO'))
      const allRisksAcknowledged = nextOrder.garments.every((garment) => garment.riskFlags.length === 0 || evidenceAfter.some((candidate) =>
        candidate.orderId === nextOrder.id && candidate.garmentId === garment.id && candidate.kind === 'RISK_ACKNOWLEDGEMENT'))
      nextOrder = {
        ...nextOrder,
        pricingReadiness: {
          ...nextOrder.pricingReadiness,
          allImagesUploaded,
          allRequiredRiskAcknowledgementsConfirmed: allRisksAcknowledged,
        },
      }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? nextOrder : candidate)
      this.events = [...this.events, createImmutableEvent({
        id: nextId('EVT'), type: 'EVIDENCE_ATTACHED', orderId: order.id, garmentId: input.garmentId,
        operatorId: input.capturedBy, occurredAt: capturedAt, metadata: { evidenceId: item.id, kind: item.kind },
      })]
      await this.persist()
      return item
    },

    async confirmPricing(orderId: string, operatorId: string, suppliedKey?: string): Promise<Order> {
      const order = orderById(this.orders, orderId)
      const key = commandKey('pricing', suppliedKey)
      assertCommandKey('CONFIRM_PRICING', key, this.processedCommandKeys)
      const occurredAt = now()
      const events: ImmutableDomainEvent[] = []
      const garments = order.garments.map((garment) => {
        const result = transitionGarment({
          garment, transitionId: 'CONFIRM_PRICING', guardContext: guardContextFor(order, garment),
          operatorId, occurredAt, eventId: nextId('EVT'), idempotencyKey: `${key}:${garment.id}`,
          metadata: { confirmedAmount: order.amounts.confirmed },
        })
        if (result.event) events.push(result.event)
        return result.garment
      })
      const updated = { ...order, garments }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updated : candidate)
      this.events = [...this.events, ...events]
      this.processedCommandKeys = [...this.processedCommandKeys, key]
      await this.persist()
      return updated
    },

    async simulatePaymentAndActivateLabels(
      orderId: string,
      operatorId: string,
      method: NonNullable<Order['payment']['method']> = 'DEMO',
      suppliedKey?: string,
    ): Promise<Order> {
      const order = orderById(this.orders, orderId)
      const key = commandKey('payment', suppliedKey)
      assertCommandKey('SIMULATE_PAYMENT', key, this.processedCommandKeys)
      if (order.payment.status !== 'PENDING' && order.payment.status !== 'FAILED') {
        throw new DomainRuleError('PAYMENT_STATE_BLOCKED', `Cannot start payment from ${order.payment.status}`)
      }
      if (order.amounts.recalculated !== order.amounts.confirmed) {
        throw new DomainRuleError('AMOUNT_CHANGED', 'Recalculated and confirmed amounts must match')
      }
      const member = this.members.find((candidate) => candidate.id === order.memberId)
      if (method === 'MEMBER_BALANCE' && (!member || member.balance < order.amounts.confirmed)) {
        throw new DomainRuleError('INSUFFICIENT_BALANCE', 'Member balance is insufficient')
      }

      const occurredAt = now()
      const events: ImmutableDomainEvent[] = [createImmutableEvent({
        id: nextId('EVT'), type: order.payment.status === 'FAILED' ? 'PAYMENT_RETRIED' : 'PAYMENT_STARTED',
        orderId, operatorId, occurredAt, idempotencyKey: `${key}:payment-start`, metadata: { method },
      })]
      let updated: Order = {
        ...order,
        payment: { ...order.payment, status: 'SUCCEEDED', method, amount: order.amounts.confirmed, paidAt: occurredAt },
      }
      let garments = updated.garments.map((garment) => {
        const result = transitionGarment({
          garment, transitionId: 'SIMULATE_PAYMENT', guardContext: guardContextFor(updated, garment),
          operatorId, occurredAt, eventId: nextId('EVT'), idempotencyKey: `${key}:paid:${garment.id}`,
          metadata: { paymentStatus: 'SUCCEEDED', amount: updated.amounts.confirmed },
        })
        if (result.event) events.push(result.event)
        return result.garment
      })
      updated = { ...updated, garments }
      garments = updated.garments.map((garment) => {
        const labels = garment.labels.map((label) => label.version === Math.max(...garment.labels.map((item) => item.version))
          ? { ...label, status: 'ACTIVE' as const, activatedAt: occurredAt }
          : label)
        const withLabel = { ...garment, labels }
        const result = transitionGarment({
          garment: withLabel, transitionId: 'ACTIVATE_LABELS', guardContext: guardContextFor(updated, withLabel),
          operatorId: 'system', occurredAt, eventId: nextId('EVT'), idempotencyKey: `${key}:label:${garment.id}`,
          metadata: { activeLabelVersion: activeLabelOf(withLabel)?.version ?? 1 },
        })
        if (result.event) events.push(result.event)
        return result.garment
      })
      events.push(
        createImmutableEvent({ id: nextId('EVT'), type: 'PRINT_STARTED', orderId, operatorId: 'system', occurredAt, metadata: { printJobId: order.initialLabelPrintJob.id } }),
        createImmutableEvent({
          id: nextId('EVT'), type: 'PRINT_SUCCEEDED', orderId, operatorId: 'system', occurredAt,
          metadata: { printJobId: order.initialLabelPrintJob.id, successCount: garments.length, failedCount: 0 },
        }),
      )
      updated = {
        ...updated,
        garments,
        initialLabelPrintJob: { ...updated.initialLabelPrintJob, status: 'SUCCEEDED', successCount: garments.length, failedCount: 0 },
      }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updated : candidate)
      if (method === 'MEMBER_BALANCE' && member) {
        this.members = this.members.map((candidate) => candidate.id === member.id
          ? { ...candidate, balance: roundMoney(candidate.balance - updated.amounts.confirmed) }
          : candidate)
      }
      this.events = [...this.events, ...events]
      this.processedCommandKeys = [...this.processedCommandKeys, key]
      this.notifications = [{
        id: nextId('NTF'), kind: 'PAYMENT', title: '支付与标签激活成功',
        body: `${order.id} 已支付 ¥${updated.amounts.confirmed.toFixed(2)}，${garments.length} 个标签已激活。`,
        createdAt: occurredAt, orderId,
      }, ...this.notifications]
      await this.persist()
      return updated
    },

    async confirmTransition(input: ConfirmTransitionInput): Promise<Order> {
      let order = orderById(this.orders, input.orderId)
      if (input.dispatchBatchId) order = { ...order, dispatchBatchId: input.dispatchBatchId }
      const definition = TRANSITIONS_BY_ID[input.transitionId]
      const key = input.idempotencyKey ?? (definition.idempotent ? undefined : commandKey(input.transitionId.toLowerCase()))
      assertCommandKey(input.transitionId, key, this.processedCommandKeys)
      const selected = new Set(input.garmentIds ?? order.garments
        .filter((garment) => garment.workflowState === definition.from || (definition.idempotent && garment.workflowState === definition.to))
        .map((garment) => garment.id))
      if (selected.size === 0) throw new DomainRuleError('NO_TRANSITION_TARGETS', `No garments can run ${input.transitionId}`)
      const occurredAt = now()
      const events: ImmutableDomainEvent[] = []
      const garments = order.garments.map((garment) => {
        if (!selected.has(garment.id)) return garment
        const result = transitionGarment({
          garment, transitionId: input.transitionId,
          guardContext: guardContextFor(order, garment, input.guardContext),
          operatorId: input.operatorId, occurredAt, eventId: nextId('EVT'),
          idempotencyKey: key ? `${key}:${garment.id}` : undefined, metadata: input.metadata,
        })
        if (result.event) events.push(result.event)
        return result.garment
      })
      const updated = { ...order, garments }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updated : candidate)
      this.events = [...this.events, ...events]
      if (key) this.processedCommandKeys = [...this.processedCommandKeys, key]
      await this.persist()
      return updated
    },

    async reconcileBatch(input: ReconcileBatchActionInput): Promise<BatchReconciliation> {
      const expectedGarmentIds = input.expectedGarmentIds ?? (input.transitionId
        ? orderById(this.orders, input.orderId).garments
          .filter((garment) => garment.workflowState === TRANSITIONS_BY_ID[input.transitionId!].from)
          .map((garment) => garment.id)
        : undefined)
      const reconciliation = reconcileBatchReads({
        id: nextId('REC'), orderId: input.orderId, orders: this.orders, reads: input.reads,
        transitionId: input.transitionId, expectedGarmentIds, createdAt: now(),
      })
      this.reconciliations = [reconciliation, ...this.reconciliations].slice(0, 30)
      await this.persist()
      return reconciliation
    },

    async confirmReconciledTransition(
      reconciliationId: string,
      operatorId: string,
      suppliedKey?: string,
      extraGuardContext: TransitionGuardContext = {},
    ): Promise<Order> {
      const reconciliation = this.reconciliations.find((candidate) => candidate.id === reconciliationId)
      if (!reconciliation) throw new DomainRuleError('RECONCILIATION_NOT_FOUND', `Batch ${reconciliationId} was not found`)
      if (!reconciliation.transitionId) throw new DomainRuleError('TRANSITION_REQUIRED', 'This batch is a return receipt, not a state transition')
      assertReconciliationCanConfirm(reconciliation)
      const order = await this.confirmTransition({
        orderId: reconciliation.orderId,
        transitionId: reconciliation.transitionId,
        garmentIds: reconciliation.expectedGarmentIds,
        operatorId,
        idempotencyKey: suppliedKey,
        guardContext: { ...extraGuardContext, scanLabelStatus: 'ACTIVE', scanConfirmed: true },
        metadata: { reconciliationId },
        // Mobile batches are created at the explicit confirm boundary, never
        // by scan recognition. This satisfies SEND_TO_PLANT's contract guard.
        dispatchBatchId: reconciliation.transitionId === 'SEND_TO_PLANT'
          ? `B-MOBILE-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${reconciliation.id.slice(-6)}`
          : undefined,
      })
      const confirmedAt = now()
      this.reconciliations = this.reconciliations.map((candidate) => candidate.id === reconciliation.id
        ? deepFreeze({ ...candidate, confirmedAt }) as BatchReconciliation
        : candidate)
      await this.persist()
      return order
    },

    async confirmReturnReconciliation(reconciliationId: string, operatorId: string): Promise<Order> {
      const reconciliation = this.reconciliations.find((candidate) => candidate.id === reconciliationId)
      if (!reconciliation) throw new DomainRuleError('RECONCILIATION_NOT_FOUND', `Batch ${reconciliationId} was not found`)
      if (reconciliation.transitionId) throw new DomainRuleError('RETURN_BATCH_EXPECTED', 'Use confirmReconciledTransition for a workflow transition')
      assertReconciliationCanConfirm(reconciliation)
      const order = orderById(this.orders, reconciliation.orderId)
      if (reconciliation.expectedGarmentIds.some((id) => garmentById(order, id).workflowState !== 'WAITING_SHELF')) {
        throw new DomainRuleError('RETURN_STATE_BLOCKED', 'Store-return confirmation only accepts WAITING_SHELF garments')
      }
      const confirmedAt = now()
      const expected = new Set(reconciliation.expectedGarmentIds)
      const updated: Order = {
        ...order,
        garments: order.garments.map((garment) => expected.has(garment.id) ? { ...garment, returnedToStoreAt: confirmedAt } : garment),
      }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updated : candidate)
      this.reconciliations = this.reconciliations.map((candidate) => candidate.id === reconciliation.id
        ? deepFreeze({ ...candidate, confirmedAt }) as BatchReconciliation
        : candidate)
      this.events = [...this.events, createImmutableEvent({
        id: nextId('EVT'), type: 'BATCH_RECONCILED', orderId: order.id, operatorId, occurredAt: confirmedAt,
        metadata: { reconciliationId, garmentCount: expected.size, purpose: 'STORE_RETURN' },
      })]
      this.notifications = [{
        id: nextId('NTF'), kind: 'RETURN', title: '回件对账完成',
        body: `${order.id} 已完成 ${expected.size}/${expected.size} 回件确认。`, createdAt: confirmedAt, orderId: order.id,
      }, ...this.notifications]
      await this.persist()
      return updated
    },

    async recordFactoryInspection(
      orderId: string,
      garmentId: string,
      input: Omit<FactoryInspection, 'id' | 'inspectedAt' | 'syncedToStoreAt'> & Partial<Pick<FactoryInspection, 'id' | 'inspectedAt' | 'syncedToStoreAt'>>,
      registerIssueAsException = true,
      suppliedKey?: string,
    ): Promise<Garment> {
      const order = orderById(this.orders, orderId)
      let garment = garmentById(order, garmentId)
      if (!garment.washCompletedAt) throw new DomainRuleError('WASH_NOT_COMPLETE', 'Factory inspection requires completed washing')
      if (input.status === 'ISSUE' && (!input.issueType?.trim() || !input.note?.trim())) {
        throw new DomainRuleError('INSPECTION_ISSUE_DETAILS_REQUIRED', 'Issue type and note are required')
      }
      const occurredAt = input.inspectedAt ?? now()
      const inspection: FactoryInspection = deepFreeze({
        ...input,
        id: input.id ?? nextId('FINSP'),
        inspectedAt: occurredAt,
        syncedToStoreAt: input.syncedToStoreAt ?? occurredAt,
        evidenceIds: [...input.evidenceIds],
      }) as FactoryInspection
      garment = { ...garment, factoryInspections: [...garment.factoryInspections, inspection] }
      const events: ImmutableDomainEvent[] = [createImmutableEvent({
        id: nextId('EVT'), type: 'FACTORY_INSPECTION_RECORDED', orderId, garmentId,
        operatorId: input.inspectorId, occurredAt, metadata: { inspectionId: inspection.id, status: inspection.status },
      })]
      if (inspection.status === 'ISSUE' && registerIssueAsException && garment.workflowState !== 'EXCEPTION' && canRegisterException(garment.workflowState)) {
        const key = commandKey('factory-exception', suppliedKey)
        if (this.processedCommandKeys.includes(key)) throw new DomainRuleError('DUPLICATE_COMMAND', `Command ${key} was already processed`)
        const result = registerGarmentException({
          garment, reason: `${inspection.issueType}: ${inspection.note}`, operatorId: input.inspectorId,
          occurredAt, eventId: nextId('EVT'), idempotencyKey: key,
        })
        garment = result.garment
        if (result.event) events.push(result.event)
        this.processedCommandKeys = [...this.processedCommandKeys, key]
      }
      const updatedOrder = replaceGarment(order, garment)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updatedOrder : candidate)
      this.events = [...this.events, ...events]
      if (inspection.status === 'ISSUE') {
        this.notifications = [{
          id: nextId('NTF'), kind: 'EXCEPTION', title: '工厂二检异常',
          body: `${garment.shortCode}：${inspection.issueType}，${inspection.note}`, createdAt: occurredAt, orderId, garmentId,
        }, ...this.notifications]
      }
      await this.persist()
      return garment
    },

    async overrideGarmentInfoFromFactory(
      orderId: string,
      garmentId: string,
      patch: Partial<Pick<Garment, 'category' | 'color' | 'material' | 'service' | 'conditionNote'>>,
      operatorId: string,
      reason: string,
    ): Promise<Garment> {
      const order = orderById(this.orders, orderId)
      const garment = garmentById(order, garmentId)
      if (!garment.washCompletedAt) throw new DomainRuleError('WASH_NOT_COMPLETE', 'Factory override requires completed washing')
      if (!reason.trim()) throw new DomainRuleError('OVERRIDE_REASON_REQUIRED', 'Factory override reason is required')
      if (Object.keys(patch).length === 0) throw new DomainRuleError('OVERRIDE_EMPTY', 'Provide at least one field to override')
      const occurredAt = now()
      const before = {
        category: garment.category, color: garment.color, material: garment.material,
        service: garment.service, conditionNote: garment.conditionNote,
      }
      const after = { ...before, ...patch }
      const override: FactoryInformationOverride = deepFreeze({
        id: nextId('FOVR'), operatorId, occurredAt, reason, before, after,
      }) as FactoryInformationOverride
      // shortCode, price and workflowState are intentionally absent from the patch type.
      const updated: Garment = { ...garment, ...patch, factoryOverrides: [...garment.factoryOverrides, override] }
      const nextOrder = replaceGarment(order, updated)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? nextOrder : candidate)
      this.events = [...this.events, createImmutableEvent({
        id: nextId('EVT'), type: 'FACTORY_INFORMATION_OVERRIDDEN', orderId, garmentId, operatorId, occurredAt,
        metadata: { overrideId: override.id, reason, fields: Object.keys(patch) },
      })]
      await this.persist()
      return updated
    },

    async registerException(
      orderId: string,
      garmentId: string,
      reason: string,
      operatorId: string,
      suppliedKey?: string,
    ): Promise<Garment> {
      const order = orderById(this.orders, orderId)
      const garment = garmentById(order, garmentId)
      const key = commandKey('exception', suppliedKey)
      if (this.processedCommandKeys.includes(key)) throw new DomainRuleError('DUPLICATE_COMMAND', `Command ${key} was already processed`)
      const result = registerGarmentException({ garment, reason, operatorId, occurredAt: now(), eventId: nextId('EVT'), idempotencyKey: key })
      const updatedOrder = replaceGarment(order, result.garment)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updatedOrder : candidate)
      if (result.event) this.events = [...this.events, result.event]
      this.processedCommandKeys = [...this.processedCommandKeys, key]
      await this.persist()
      return result.garment
    },

    async resolveException(
      orderId: string,
      garmentId: string,
      resolutionNote: string,
      operatorId: string,
      suppliedKey?: string,
    ): Promise<Garment> {
      const order = orderById(this.orders, orderId)
      const garment = garmentById(order, garmentId)
      const key = commandKey('resolve-exception', suppliedKey)
      if (this.processedCommandKeys.includes(key)) throw new DomainRuleError('DUPLICATE_COMMAND', `Command ${key} was already processed`)
      const result = resolveGarmentException({
        garment, resolutionNote, operatorId, occurredAt: now(), eventId: nextId('EVT'), idempotencyKey: key,
      })
      const updatedOrder = replaceGarment(order, result.garment)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? updatedOrder : candidate)
      if (result.event) this.events = [...this.events, result.event]
      this.processedCommandKeys = [...this.processedCommandKeys, key]
      await this.persist()
      return result.garment
    },

    async shelveGarment(
      orderId: string,
      garmentId: string,
      rackId: string,
      operatorId: string,
      suppliedKey?: string,
    ): Promise<Order> {
      let order = orderById(this.orders, orderId)
      const garment = garmentById(order, garmentId)
      const rack = this.racks.find((candidate) => candidate.id === rackId)
      if (!rack) throw new DomainRuleError('RACK_NOT_FOUND', `Rack ${rackId} was not found`)
      const key = commandKey('shelf', suppliedKey)
      assertCommandKey('BIND_RACK', key, this.processedCommandKeys)
      const occurredAt = now()
      const result = transitionGarment({
        garment, transitionId: 'BIND_RACK',
        guardContext: guardContextFor(order, garment, { rackStatus: rack.status }),
        operatorId, occurredAt, eventId: nextId('EVT'), idempotencyKey: `${key}:${garment.id}`, metadata: { rackId },
      })
      let shelvedGarment: Garment = {
        ...result.garment,
        rackBindings: [...result.garment.rackBindings, { rackId, boundAt: occurredAt, boundBy: operatorId, active: true }],
      }
      order = replaceGarment(order, shelvedGarment)
      const events: ImmutableDomainEvent[] = result.event ? [result.event] : []
      this.racks = this.racks.map((candidate) => candidate.id === rack.id
        ? { ...candidate, status: 'OCCUPIED', activeGarmentId: garment.id }
        : candidate)

      if (order.garments.every((candidate) => candidate.workflowState === 'SHELVED')) {
        const readyEvents: ImmutableDomainEvent[] = []
        const readyGarments = order.garments.map((candidate) => {
          const ready = transitionGarment({
            garment: candidate, transitionId: 'MARK_READY_FOR_PICKUP',
            guardContext: guardContextFor(order, candidate, { allOrderGarmentsStatus: 'SHELVED' }),
            operatorId: 'system', occurredAt, eventId: nextId('EVT'), metadata: { allOrderGarmentsStatus: 'SHELVED' },
          })
          if (ready.event) readyEvents.push(ready.event)
          return ready.garment
        })
        order = { ...order, garments: readyGarments }
        events.push(...readyEvents)
        this.notifications = [{
          id: nextId('NTF'), kind: 'PICKUP', title: '订单待取',
          body: `${order.id} 已 ${order.garments.length}/${order.garments.length} 上架，可通知顾客自取。`,
          createdAt: occurredAt, orderId: order.id,
        }, ...this.notifications]
      }
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? order : candidate)
      this.events = [...this.events, ...events]
      this.processedCommandKeys = [...this.processedCommandKeys, key]
      await this.persist()
      return order
    },

    async verifyPickupGarment(orderId: string, scanValue: string, operatorId: string): Promise<{
      order: Order
      verifiedCount: number
      totalCount: number
    }> {
      let order = orderById(this.orders, orderId)
      const match = this.orders.flatMap((candidate) => candidate.garments).find((garment) =>
        garment.shortCode === scanValue || garment.labels.some((label) => label.barcode === scanValue || label.epc === scanValue))
      if (!match) throw new DomainRuleError('SCAN_UNKNOWN', `Unknown pickup label ${scanValue}`)
      if (match.orderId !== order.id) throw new DomainRuleError('PICKUP_CROSS_ORDER', `${scanValue} belongs to another order`)
      const label = activeLabelOf(match)
      if (!label) throw new DomainRuleError('INACTIVE_LABEL', `${scanValue} has no ACTIVE label`)
      const occurredAt = now()
      const result = transitionGarment({
        garment: match, transitionId: 'VERIFY_PICKUP_GARMENT',
        guardContext: guardContextFor(order, match, { pickupCurrentScanMatchesOrder: true, scanLabelStatus: 'ACTIVE' }),
        operatorId, occurredAt, eventId: nextId('EVT'), metadata: { scanValue },
      })
      order = replaceGarment(order, result.garment)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? order : candidate)
      if (result.event) this.events = [...this.events, result.event]
      await this.persist()
      return {
        order,
        verifiedCount: order.garments.filter((garment) => garment.workflowState === 'PICKUP_VERIFYING').length,
        totalCount: order.garments.length,
      }
    },

    async completeSelfPickup(
      orderId: string,
      operatorId: string,
      signatureUri: string,
      customerIdentityConfirmed: boolean,
      riskAndQuantityConfirmed: boolean,
      suppliedKey?: string,
    ): Promise<Order> {
      let order = orderById(this.orders, orderId)
      if (!signatureUri.trim()) throw new DomainRuleError('SIGNATURE_REQUIRED', 'Customer signature is required')
      const verifiedCount = order.garments.filter((garment) => garment.workflowState === 'PICKUP_VERIFYING').length
      if (verifiedCount !== order.garments.length) {
        throw new DomainRuleError('PICKUP_COUNT_MISMATCH', `Only ${verifiedCount}/${order.garments.length} garments are verified`)
      }
      const key = commandKey('pickup', suppliedKey)
      assertCommandKey('COMPLETE_PICKUP', key, this.processedCommandKeys)
      const occurredAt = now()
      order = { ...order, customerIdentityConfirmed, pickupRiskAndQuantityConfirmed: riskAndQuantityConfirmed }
      const events: ImmutableDomainEvent[] = []
      const garments = order.garments.map((garment) => {
        const result = transitionGarment({
          garment, transitionId: 'COMPLETE_PICKUP',
          guardContext: guardContextFor(order, garment, {
            pickupVerifiedGarmentCount: verifiedCount,
            orderGarmentCount: order.garments.length,
            pickupCustomerIdentityConfirmed: customerIdentityConfirmed,
            pickupRiskAndQuantityConfirmed: riskAndQuantityConfirmed,
          }),
          operatorId, occurredAt, eventId: nextId('EVT'), idempotencyKey: `${key}:${garment.id}`,
          metadata: { verifiedCount, totalCount: order.garments.length },
        })
        if (result.event) events.push(result.event)
        return result.garment
      })
      const signature: Evidence = deepFreeze({
        id: nextId('EVD'), orderId, kind: 'PICKUP_SIGNATURE', uri: signatureUri,
        note: `${verifiedCount}/${order.garments.length} 自取签收`, capturedBy: operatorId,
        capturedAt: occurredAt, metadata: { verifiedCount, totalCount: order.garments.length },
      }) as Evidence
      order = { ...order, garments }
      const releasedRackIds = new Set(order.garments.flatMap((garment) => garment.rackBindings.filter((binding) => binding.releasedAt === occurredAt).map((binding) => binding.rackId)))
      this.racks = this.racks.map((rack) => releasedRackIds.has(rack.id)
        ? { ...rack, status: 'AVAILABLE', activeGarmentId: undefined }
        : rack)
      this.orders = this.orders.map((candidate) => candidate.id === order.id ? order : candidate)
      this.evidence = [...this.evidence, signature]
      this.events = [...this.events, ...events, createImmutableEvent({
        id: nextId('EVT'), type: 'EVIDENCE_ATTACHED', orderId, operatorId, occurredAt,
        metadata: { evidenceId: signature.id, kind: signature.kind },
      })]
      this.processedCommandKeys = [...this.processedCommandKeys, key]
      await this.persist()
      return order
    },

    async markNotificationRead(notificationId: string, readAt = now()): Promise<AppNotification> {
      const notification = this.notifications.find((candidate) => candidate.id === notificationId)
      if (!notification) throw new DomainRuleError('NOTIFICATION_NOT_FOUND', `Notification ${notificationId} was not found`)
      const updated = notification.readAt ? notification : { ...notification, readAt }
      this.notifications = this.notifications.map((candidate) => candidate.id === notification.id ? updated : candidate)
      await this.persist()
      return updated
    },

    async markAllNotificationsRead(readAt = now()): Promise<void> {
      this.notifications = this.notifications.map((notification) => notification.readAt ? notification : { ...notification, readAt })
      await this.persist()
    },

    async updateDeviceSettings(patch: Partial<DeviceSettings>): Promise<DeviceSettings> {
      this.deviceSettings = {
        ...this.deviceSettings,
        ...patch,
        scanner: { ...this.deviceSettings.scanner, ...patch.scanner },
        rfidReader: { ...this.deviceSettings.rfidReader, ...patch.rfidReader },
        camera: { ...this.deviceSettings.camera, ...patch.camera },
        printer: { ...this.deviceSettings.printer, ...patch.printer },
        network: { ...this.deviceSettings.network, ...patch.network },
      }
      await this.persist()
      return this.deviceSettings
    },
  },
})
